import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Eye, Globe, MessageCircle, CalendarDays, TrendingUp } from "lucide-react";
import api from "@/lib/api";

const PERIODS = [
  { days: 7, label: "7 jours" },
  { days: 30, label: "30 jours" },
  { days: 90, label: "90 jours" },
];

export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get(`/admin/analytics?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ series: [], sources: [], top_pages: [], top_products: [], visitors: 0, pageviews: 0, whatsapp_clicks: 0, reserver_clicks: 0 }));
  }, [days]);

  if (!data) return null;

  const counters = [
    { icon: Eye, label: "Visiteurs uniques", value: data.visitors, testId: "analytics-visitors" },
    { icon: TrendingUp, label: "Pages vues", value: data.pageviews, testId: "analytics-pageviews" },
    { icon: MessageCircle, label: "Clics WhatsApp", value: data.whatsapp_clicks, testId: "analytics-whatsapp" },
    { icon: CalendarDays, label: "Clics Réserver", value: data.reserver_clicks, testId: "analytics-reserver" },
  ];

  return (
    <div className="mt-10" data-testid="analytics-panel">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          <Globe className="h-4 w-4" strokeWidth={1.5} />
          Audience & performance
        </h3>
        <div className="flex gap-2" data-testid="analytics-periods">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              data-testid={`analytics-period-${p.days}`}
              className={`rounded-md border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                days === p.days
                  ? "border-copper bg-copper text-ivory"
                  : "border-white/10 text-ivory/50 hover:border-copper/50 hover:text-ivory"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {counters.map((c) => (
          <div key={c.label} className="rounded-lg border border-white/10 bg-coal/60 p-5" data-testid={c.testId}>
            <c.icon className="mb-3 h-4 w-4 text-copper" strokeWidth={1.5} />
            <p className="text-xl font-bold tracking-tight text-ivory">{c.value}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-ivory/40">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-lg border border-white/10 bg-coal/60 p-6" data-testid="analytics-chart">
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-ivory/50">
          Visites par jour
        </p>
        {(data.series || []).length === 0 ? (
          <p className="text-sm text-ivory/40">
            Aucune visite enregistrée sur la période. Les statistiques apparaissent dès que des
            visiteurs acceptent les cookies de mesure d'audience.
          </p>
        ) : (
          <div className="h-56 min-h-56 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <AreaChart data={data.series} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="visites" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C4704F" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C4704F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "rgba(245,245,240,0.4)", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "rgba(245,245,240,0.4)", fontSize: 10 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#0F0F0F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#C6A87C" }}
                />
                <Area type="monotone" dataKey="visites" stroke="#C4704F" strokeWidth={2} fill="url(#visites)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {[
          { title: "Provenance des visiteurs", items: data.sources || [], testId: "analytics-sources" },
          { title: "Pages les plus vues", items: data.top_pages || [], testId: "analytics-pages" },
          { title: "Produits & plats les plus consultés", items: data.top_products || [], testId: "analytics-products" },
        ].map((panel) => (
          <div key={panel.title} className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid={panel.testId}>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-ivory/50">
              {panel.title}
            </p>
            {panel.items.length === 0 ? (
              <p className="text-xs text-ivory/35">Aucune donnée sur la période.</p>
            ) : (
              <ul className="space-y-2.5">
                {panel.items.map((item) => {
                  const max = panel.items[0].value || 1;
                  return (
                    <li key={item.name}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="min-w-0 flex-1 truncate text-ivory/70">{item.name}</span>
                        <span className="ml-2 shrink-0 text-gold">{item.value}</span>
                      </div>
                      <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/5">
                        <div className="h-full rounded-full bg-copper" style={{ width: `${(item.value / max) * 100}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
